import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useParams,
} from "@tanstack/react-router";

import App from "./App";
import { RouteAuthorisation } from "./components/RouteAuthorisation";
import { ErrorFallback } from "./components/ErrorFallback";
import NotFound from "./pages/NotFound/index";
import Unauthorised from "./pages/Unauthorised/index";
// Page Imports
import { Home } from "./pages/Home/index";
import { Logout } from "./pages/Logout/index";
// Staff
import { StaffCard } from "./pages/StaffCard/index";
import { StaffList } from "./pages/StaffList/index";
// Orders
import { OrderList } from "./pages/OrderList/index";
import { OrderCard } from "./pages/OrderCard/index";
// Finance
import AccountBalance from "./pages/AccountBalanceList/index";
import OutstandingInvoiceList from "./pages/OutstandingInvoiceList/index";
import OutstandingCreditNoteList from "./pages/OutstandingCreditNoteList/index";
import SettledCreditNoteList from "./pages/SettledCreditNoteList/index";
import CreditNoteCard from "./pages/CreditNoteCard/index";
import { SettledInvoiceList } from "./pages/SettledInvoiceList/index";
import InvoiceCard from "./pages/InvoiceCard/index";
// Returns
import { ReturnOrderCreate } from "./pages/ReturnOrderCreate/index";
import { ReturnOrderPendingList } from "./pages/ReturnOrderPendingList/index";
import { ReturnOrderPendingCard } from "./pages/ReturnOrderPendingCard/index";
import { ReturnOrderCompletedList } from "./pages/ReturnOrderCompletedList/index";
import { ReturnOrderCompletedCard } from "./pages/ReturnOrderCompletedCard/index";
// Draft order
import { DraftOrder } from "./pages/DraftOrder/index";
import { DraftOrderBulk } from "./pages/DraftOrderBulk/index";
// Staff Requests
import { StaffRequest } from "./pages/StaffRequest/index";
import { RequestList } from "./pages/RequestList/index";
import { RequestHistory } from "./pages/RequestHistory/index";
// Wardrobe
import { WardrobeList } from "./pages/WardrobeList/index";
import { WardrobeItemList } from "./pages/WardrobeItemList/index";
import { ItemList } from "./pages/ItemList/index";
// Contracts
import { ContractCard } from "./pages/ContractCard/index";
import { ContractList } from "./pages/ContractList/index";
// Entitlement
import { EntitlementList } from "./pages/EntitlementList/index";
import EntitlementCard from "./pages/EntitlementCard/index";
// User
import { UserAccount } from "./pages/UserAccount/index";
// Wardrobe Request
import { WardrobeRequest } from "./pages/WardrobeRequest/index";
// Reports
import { Reports } from "./pages/Reports/index";
// Support
import { Support } from "./pages/Support/index";
import { useBulkOrderMode } from "./hooks/useAuthorisation";
import { Navigate } from "./components/Navigate";
import { parseSearch, stringifySearch } from "./utils/searchSerialisation";

/**
 * The root route renders the application shell (providers + PageLayout) and an
 * <Outlet /> for the matched page.
 *
 * `validateSearch` is deliberately permissive: TanStack Router drops search
 * params that no route declares, and this app reads free-form query strings in
 * several places (see useUrlFilterState). Passing them straight through
 * preserves the react-router behaviour. Routes can still narrow this later by
 * declaring their own validateSearch.
 */
const rootRoute = createRootRoute({
  validateSearch: (search) => search,
  component: App,
  notFoundComponent: NotFound,
});

/**
 * A pathless layout route that gates everything beneath it on the user's
 * roles. This mirrors the nested <Route element={<RouteAuthorisation />}>
 * boundaries the app used under react-router.
 */
const guard = (id, allowedRoles, getParentRoute = () => rootRoute) =>
  createRoute({
    getParentRoute,
    id,
    component: function Guard() {
      return <RouteAuthorisation allowedRoles={allowedRoles} />;
    },
  });

const page = (getParentRoute, path, component) =>
  createRoute({ getParentRoute, path, component });

/** Replaces <Route element={<Navigate replace to="..." />} />. */
const redirectTo = (getParentRoute, path, to) =>
  createRoute({
    getParentRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to, replace: true });
    },
  });

// --- Ungated ---------------------------------------------------------------
const indexRoute = page(() => rootRoute, "/", Home);
const unauthorisedRoute = page(() => rootRoute, "/unauthorised", Unauthorised);
const accountRoute = page(() => rootRoute, "/account", UserAccount);
const logoutRoute = page(() => rootRoute, "/logout", Logout);
const supportRoute = page(() => rootRoute, "/support", Support);

// --- Staff -----------------------------------------------------------------
const staffGuard = guard("staff-guard", ["allowstaff"]);
const staffCreateGuard = guard(
  "staff-create-guard",
  ["staffCreate"],
  () => staffGuard,
);

const staffListRoute = page(
  () => staffGuard,
  "/staff",
  () => <StaffList status="all" />,
);
const staffDetailRoute = page(() => staffGuard, "/staff/$id", StaffCard);
const staffActiveRoute = page(
  () => staffGuard,
  "/staff/active",
  () => <StaffList status="active" />,
);
const staffInactiveRoute = page(
  () => staffGuard,
  "/staff/inactive",
  () => <StaffList status="inactive" />,
);
const staffAddRoute = page(
  () => staffCreateGuard,
  "/staff/add",
  () => <StaffCard newStaff />,
);

// --- Orders ----------------------------------------------------------------
const ordersGuard = guard("orders-guard", ["alloworders"]);

const DraftOrderRoute = () => {
  const bulkOrderMode = useBulkOrderMode();
  return bulkOrderMode ? <Navigate to="/draft-bulk" replace /> : <DraftOrder />;
};

const DraftOrderDetailRoute = () => {
  const bulkOrderMode = useBulkOrderMode();
  const { id } = useParams({ strict: false });
  return bulkOrderMode ? (
    <Navigate to={`/draft-bulk/${id}`} replace />
  ) : (
    <DraftOrder />
  );
};

const draftRoute = page(() => ordersGuard, "/draft", DraftOrderRoute);
const draftDetailRoute = page(
  () => ordersGuard,
  "/draft/$id",
  DraftOrderDetailRoute,
);
const draftBulkRoute = page(() => ordersGuard, "/draft-bulk", DraftOrderBulk);
const draftBulkDetailRoute = page(
  () => ordersGuard,
  "/draft-bulk/$id",
  DraftOrderBulk,
);
const receivedRedirectRoute = redirectTo(
  () => ordersGuard,
  "/received",
  "/received-orders",
);
const receivedDetailRoute = page(() => ordersGuard, "/received/$id", OrderCard);
const draftOrdersRoute = page(
  () => ordersGuard,
  "/draft-orders",
  () => <OrderList status="draft" />,
);
const receivedOrdersRoute = page(
  () => ordersGuard,
  "/received-orders",
  () => <OrderList status="received" />,
);
const completedOrdersRoute = page(
  () => ordersGuard,
  "/completed-orders",
  () => <OrderList status="completed" />,
);

// --- Staff requests --------------------------------------------------------
const requestGuard = guard("request-guard", ["staffRequestCreate"]);

const requestRoute = page(() => requestGuard, "/request", StaffRequest);
const requestDetailRoute = page(
  () => requestGuard,
  "/request/$id",
  StaffRequest,
);
const draftRequestsRoute = page(
  () => requestGuard,
  "/draft-requests",
  () => <RequestList status="draft" />,
);
const submittedRequestsRoute = page(
  () => requestGuard,
  "/submitted-requests",
  () => <RequestList status="submitted" />,
);
const requestHistoryRoute = page(
  () => requestGuard,
  "/request-history",
  RequestHistory,
);

// --- Request approval ------------------------------------------------------
const approvalGuard = guard("approval-guard", ["staffRequestApprove"]);

const approvalsRoute = page(
  () => approvalGuard,
  "/approvals",
  () => <RequestList status="approvals" />,
);
const approvedOrdersRoute = page(
  () => approvalGuard,
  "/approved-orders",
  () => <OrderList status="approved-orders" />,
);
const approvedInvoicesRoute = page(
  () => approvalGuard,
  "/approved-invoices",
  () => <OrderList status="approved-invoices" />,
);

// --- Finance ---------------------------------------------------------------
const financeGuard = guard("finance-guard", ["allowfinances"]);

const balanceRoute = page(() => financeGuard, "/balance", AccountBalance);
const invoicesRedirectRoute = redirectTo(
  () => financeGuard,
  "/invoices",
  "/completed-orders",
);
const outstandingInvoicesRoute = page(
  () => financeGuard,
  "/invoices/outstanding",
  OutstandingInvoiceList,
);
const settledInvoicesRoute = page(
  () => financeGuard,
  "/invoices/settled",
  SettledInvoiceList,
);
const creditRedirectRoute = redirectTo(
  () => financeGuard,
  "/credit",
  "/credit/outstanding",
);
const outstandingCreditRoute = page(
  () => financeGuard,
  "/credit/outstanding",
  OutstandingCreditNoteList,
);
const settledCreditRoute = page(
  () => financeGuard,
  "/credit/settled",
  SettledCreditNoteList,
);
const creditDetailRoute = page(
  () => financeGuard,
  "/credit/$id",
  CreditNoteCard,
);

// Invoice detail is reachable by either role.
const invoiceDetailGuard = guard("invoice-detail-guard", [
  "alloworders",
  "allowfinances",
]);
const invoiceDetailRoute = page(
  () => invoiceDetailGuard,
  "/invoices/$id",
  InvoiceCard,
);

// --- Returns ---------------------------------------------------------------
const returnsGuard = guard("returns-guard", ["allowreturns"]);

const returnsRoute = page(
  () => returnsGuard,
  "/returns",
  ReturnOrderCompletedList,
);
const returnsNewRoute = page(
  () => returnsGuard,
  "/returns/new",
  ReturnOrderCreate,
);
const returnsCompletedRoute = page(
  () => returnsGuard,
  "/returns/completed",
  ReturnOrderCompletedList,
);
const returnsPendingRoute = page(
  () => returnsGuard,
  "/returns/pending",
  ReturnOrderPendingList,
);
const returnsPendingDetailRoute = page(
  () => returnsGuard,
  "/returns/pending/$id",
  ReturnOrderPendingCard,
);
const returnsCompletedDetailRoute = page(
  () => returnsGuard,
  "/returns/completed/$id",
  ReturnOrderCompletedCard,
);

// --- Entitlement -----------------------------------------------------------
const entitlementGuard = guard("entitlement-guard", ["allowentitlement"]);

const entitlementRedirectRoute = redirectTo(
  () => entitlementGuard,
  "/entitlement",
  "/entitlement/remaining",
);
const entitlementRemainingRoute = page(
  () => entitlementGuard,
  "/entitlement/remaining",
  () => <EntitlementList type="remaining" />,
);
const entitlementCompleteRoute = page(
  () => entitlementGuard,
  "/entitlement/complete",
  () => <EntitlementList type="complete" />,
);
const entitlementExceededRoute = page(
  () => entitlementGuard,
  "/entitlement/exceeded",
  () => <EntitlementList type="exceeded" />,
);
const entitlementPredictedRoute = page(
  () => entitlementGuard,
  "/entitlement/predicted",
  () => <EntitlementList type="predicted" />,
);
const entitlementDetailRoute = page(
  () => entitlementGuard,
  "/entitlement/$id",
  EntitlementCard,
);

// --- Wardrobe --------------------------------------------------------------
const wardrobeGuard = guard("wardrobe-guard", ["allowwardrobe"]);

const wardrobesRoute = page(() => wardrobeGuard, "/wardrobes", WardrobeList);
const wardrobeRequestRoute = page(
  () => wardrobeGuard,
  "/wardrobe-request",
  WardrobeRequest,
);
const wardrobeDetailRoute = page(
  () => wardrobeGuard,
  "/wardrobes/$id",
  WardrobeItemList,
);
const itemsRoute = page(() => wardrobeGuard, "/items", ItemList);

// --- Contracts -------------------------------------------------------------
const contractsGuard = guard("contracts-guard", ["allowcontracts"]);

const contractsRoute = page(() => contractsGuard, "/contracts", ContractList);
const contractDetailRoute = page(
  () => contractsGuard,
  "/contracts/$id",
  ContractCard,
);

// --- Reports ---------------------------------------------------------------
const reportsGuard = guard("reports-guard", [
  "allowgeneralreports",
  "allowfinancialreports",
]);
const generalReportsGuard = guard("general-reports-guard", [
  "allowgeneralreports",
]);
const financialReportsGuard = guard("financial-reports-guard", [
  "allowfinancialreports",
]);

const reportsRoute = page(() => reportsGuard, "/reports", Reports);
const generalReportsRoute = page(
  () => generalReportsGuard,
  "/reports/general",
  () => <Reports type="general" />,
);
const financialReportsRoute = page(
  () => financialReportsGuard,
  "/reports/financial",
  () => <Reports type="financial" />,
);

export const routeTree = rootRoute.addChildren([
  indexRoute,
  unauthorisedRoute,
  accountRoute,
  logoutRoute,
  supportRoute,
  staffGuard.addChildren([
    staffListRoute,
    staffDetailRoute,
    staffActiveRoute,
    staffInactiveRoute,
    staffCreateGuard.addChildren([staffAddRoute]),
  ]),
  ordersGuard.addChildren([
    draftRoute,
    draftDetailRoute,
    draftBulkRoute,
    draftBulkDetailRoute,
    receivedRedirectRoute,
    receivedDetailRoute,
    draftOrdersRoute,
    receivedOrdersRoute,
    completedOrdersRoute,
  ]),
  requestGuard.addChildren([
    requestRoute,
    requestDetailRoute,
    draftRequestsRoute,
    submittedRequestsRoute,
    requestHistoryRoute,
  ]),
  approvalGuard.addChildren([
    approvalsRoute,
    approvedOrdersRoute,
    approvedInvoicesRoute,
  ]),
  financeGuard.addChildren([
    balanceRoute,
    invoicesRedirectRoute,
    outstandingInvoicesRoute,
    settledInvoicesRoute,
    creditRedirectRoute,
    outstandingCreditRoute,
    settledCreditRoute,
    creditDetailRoute,
  ]),
  invoiceDetailGuard.addChildren([invoiceDetailRoute]),
  returnsGuard.addChildren([
    returnsRoute,
    returnsNewRoute,
    returnsCompletedRoute,
    returnsPendingRoute,
    returnsPendingDetailRoute,
    returnsCompletedDetailRoute,
  ]),
  entitlementGuard.addChildren([
    entitlementRedirectRoute,
    entitlementRemainingRoute,
    entitlementCompleteRoute,
    entitlementExceededRoute,
    entitlementPredictedRoute,
    entitlementDetailRoute,
  ]),
  wardrobeGuard.addChildren([
    wardrobesRoute,
    wardrobeRequestRoute,
    wardrobeDetailRoute,
    itemsRoute,
  ]),
  contractsGuard.addChildren([contractsRoute, contractDetailRoute]),
  reportsGuard.addChildren([reportsRoute]),
  generalReportsGuard.addChildren([generalReportsRoute]),
  financialReportsGuard.addChildren([financialReportsRoute]),
]);

export const router = createRouter({
  routeTree,
  parseSearch,
  stringifySearch,
  // Renders inside the shell, so a page crash leaves the nav usable.
  defaultErrorComponent: ({ error, reset }) => (
    <ErrorFallback error={error} onRetry={reset} />
  ),
});

export default router;
